import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Container, Form, Pagination } from 'react-bootstrap';
import axiosInstance from '../api/axiosInstance';
import { ClipLoader } from "react-spinners";

const Auction = () => {
    // ------------------------------------
    //  State Hooks
    // ------------------------------------
    const [sido, setSido] = useState('서울특별시');
    const [sgk, setSgk] = useState('관악구');
    const [auctionData, setAuctionData] = useState([]); // 모든 데이터를 저장할 배열
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [groupsPerPage] = useState(10); // 페이지당 공고 그룹 수
    const [expandedRows, setExpandedRows] = useState({});
    const [map, setMap] = useState(null);
    const markersRef = useRef([]); // 지도에 표시된 마커를 관리할 Ref

    // ------------------------------------
    //  Data Mappings
    // ------------------------------------
    const cities = ['서울특별시', '부산광역시', '대구광역시', '인천광역시', '광주광역시', '대전광역시', '울산광역시', '세종특별자치시', '경기도', '강원특별자치도', '충청북도', '충청남도', '전라북도', '전라남도', '경상북도', '경상남도', '제주특별자치도'];

    const districts = {
        '서울특별시': ['강남구', '강동구', '강북구', '강서구', '관악구', '광진구', '구로구', '금천구', '노원구', '도봉구', '동대문구', '동작구', '마포구', '서대문구', '서초구', '성동구', '성북구', '송파구', '양천구', '영등포구', '용산구', '은평구', '종로구', '중구', '중랑구'],
        '부산광역시': ['강서구', '금정구', '기장군', '남구', '동구', '동래구', '부산진구', '북구', '사상구', '사하구', '서구', '수영구', '연제구', '영도구', '중구', '해운대구'],
        '대구광역시': ['남구', '달서구', '달성군', '동구', '북구', '서구', '수성구', '중구', '군위군'],
        '인천광역시': ['강화군', '계양구', '남동구', '동구', '미추홀구', '부평구', '서구', '연수구', '옹진군', '중구'],
        '광주광역시': ['광산구', '남구', '동구', '북구', '서구'],
        '대전광역시': ['대덕구', '동구', '서구', '유성구', '중구'],
        '울산광역시': ['남구', '동구', '북구', '울주군', '중구'],
        '세종특별자치시': ['세종시'],
        '경기도': ['가평군', '고양시', '과천시', '광명시', '광주시', '구리시', '군포시', '김포시', '남양주시', '동두천시', '부천시', '성남시', '수원시', '시흥시', '안산시', '안성시', '안양시', '양주시', '양평군', '여주시', '연천군', '오산시', '용인시', '의왕시', '의정부시', '이천시', '파주시', '평택시', '포천시', '하남시', '화성시'],
        '강원특별자치도': ['강릉시', '고성군', '동해시', '삼척시', '속초시', '양구군', '양양군', '영월군', '원주시', '인제군', '정선군', '철원군', '춘천시', '태백시', '평창군', '홍천군', '화천군', '횡성군'],
        '충청북도': ['괴산군', '단양군', '보은군', '영동군', '옥천군', '음성군', '제천시', '증평군', '진천군', '청주시', '충주시'],
        '충청남도': ['계룡시', '공주시', '금산군', '논산시', '당진시', '보령시', '부여군', '서산시', '서천군', '아산시', '예산군', '천안시', '청양군', '태안군', '홍성군'],
        '전라북도': ['고창군', '군산시', '김제시', '남원시', '무주군', '부안군', '순창군', '완주군', '익산시', '임실군', '장수군', '전주시', '정읍시', '진안군'],
        '전라남도': ['강진군', '고흥군', '곡성군', '광양시', '구례군', '나주시', '담양군', '목포시', '무안군', '보성군', '순천시', '신안군', '여수시', '영광군', '영암군', '완도군', '장성군', '장흥군', '진도군', '함평군', '해남군', '화순군'],
        '경상북도': ['경산시', '경주시', '고령군', '구미시', '군위군', '김천시', '문경시', '봉화군', '상주시', '성주군', '안동시', '영덕군', '영양군', '영주시', '영천시', '예천군', '울릉군', '울진군', '의성군', '청도군', '청송군', '칠곡군', '포항시'],
        '경상남도': ['거제시', '거창군', '고성군', '김해시', '남해군', '밀양시', '사천시', '산청군', '양산시', '의령군', '진주시', '창녕군', '창원시', '통영시', '하동군', '함안군', '함양군', '합천군'],
        '제주특별자치도': ['서귀포시', '제주시']
    };

    // ------------------------------------
    //  Functions
    // ------------------------------------
    const handleSidoChange = (e) => {
        const newSido = e.target.value;
        setSido(newSido);
        setSgk(districts[newSido][0]);
        setCurrentPage(1); // 시/도 변경 시 1페이지로 리셋
    };

    const handleSgkChange = (e) => {
        setSgk(e.target.value);
        setCurrentPage(1); // 시/군/구 변경 시 1페이지로 리셋
    };

    const handleRowClick = (plnmNo) => {
        setExpandedRows(prev => ({
            ...prev,
            [plnmNo]: !prev[plnmNo]
        }));
    };

    const formatCurrency = (amount) => {
        if (!amount || amount === '-') {
            return '-';
        }
        return parseInt(amount).toLocaleString('ko-KR') + '원';
    };

    const formatDateTime = (dateTimeStr) => {
        if (!dateTimeStr || dateTimeStr.length !== 14) {
            return dateTimeStr;
        }

        const year = dateTimeStr.substring(0, 4);
        const month = dateTimeStr.substring(4, 6);
        const day = dateTimeStr.substring(6, 8);
        const hour = dateTimeStr.substring(8, 10);
        const minute = dateTimeStr.substring(10, 12);
        const second = dateTimeStr.substring(12, 14);

        return `${year}년 ${month}월 ${day}일 ${hour}시 ${minute}분 ${second}초`;
    };

    const fetchAuctionData = useCallback(async () => {
        setLoading(true);
        setAuctionData([]);

        const url = `https://blogapi.wajok.com/api/auction/getAuctionList`;

        try {
            const response = await axiosInstance.post(url, { sido: sido, sgk: sgk });
            const items = response.data.response.response.body.items.item;
            const newAuctionData = items.map(item => ({ ...item }));
            setAuctionData(newAuctionData);
        } catch (error) {
            console.error("데이터를 가져오는 중 오류가 발생했습니다:", error);
            alert("데이터를 가져오는데 실패했습니다. API 키나 네트워크 상태를 확인해주세요.");
            setLoading(false); // 오류 발생 시 로딩 상태 해제
        }
    }, [sido, sgk]);

    const updateMapMarkers = useCallback(async (data) => {
        const { kakao } = window;
        if (!kakao || !map) {
            setLoading(false); // kakao 맵이 없으면 로딩 상태 해제
            return;
        }

        // 기존 마커 제거
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];

        if (data.length === 0) {
            setLoading(false); // 데이터가 없으면 로딩 상태 해제
            return;
        }

        const newMarkers = [];
        const bounds = new kakao.maps.LatLngBounds();
        const geocoder = new kakao.maps.services.Geocoder();

        const promises = data.map(item => {
            return new Promise(resolve => {
                const address = item.NMRD_ADRS;
                if (address) {
                    geocoder.addressSearch(address, (result, status) => {
                        if (status === kakao.maps.services.Status.OK && result.length > 0) {
                            const coords = new kakao.maps.LatLng(result[0].y, result[0].x);

                            const marker = new kakao.maps.Marker({
                                map: map,
                                position: coords
                            });

                            const infowindow = new kakao.maps.InfoWindow({
                                content: `<div style="width:150px;text-align:center;padding:6px 0;">${item.CLTR_NM}</div>`
                            });

                            kakao.maps.event.addListener(marker, 'mouseover', () => infowindow.open(map, marker));
                            kakao.maps.event.addListener(marker, 'mouseout', () => infowindow.close());

                            bounds.extend(coords);
                            newMarkers.push(marker);
                        }
                        resolve();
                    });
                } else {
                    resolve();
                }
            });
        });

        await Promise.all(promises);

        markersRef.current = newMarkers;
        map.setBounds(bounds);
        setLoading(false);
    }, [map]);

    // ------------------------------------
    //  Derived State (Memoization)
    // ------------------------------------
    const groupedData = useMemo(() => {
        return auctionData.reduce((acc, item) => {
            const key = item.PLNM_NO;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(item);
            return acc;
        }, {});
    }, [auctionData]);

    const plnmKeys = useMemo(() => Object.keys(groupedData), [groupedData]);

    const paginatedKeys = useMemo(() => {
        const startIndex = (currentPage - 1) * groupsPerPage;
        return plnmKeys.slice(startIndex, startIndex + groupsPerPage);
    }, [plnmKeys, currentPage, groupsPerPage]);

    const paginatedData = useMemo(() => {
        return paginatedKeys.map(key => groupedData[key][0]);
    }, [paginatedKeys, groupedData]);

    const totalPages = useMemo(() => Math.ceil(plnmKeys.length / groupsPerPage), [plnmKeys.length, groupsPerPage]);

    // ------------------------------------
    //  useEffect Hooks
    // ------------------------------------
    // 시/도 또는 시/군/구 변경 시에만 데이터를 새로 가져옴
    useEffect(() => {
        fetchAuctionData();
    }, [fetchAuctionData]);

    // 전체 데이터 변경 시 지도 마커 업데이트 (최초 로드 시)
    useEffect(() => {
        if (auctionData.length > 0) {
            // 초기 로딩 시 모든 데이터가 아닌, 첫 페이지 데이터만 지도에 표시
            const initialData = plnmKeys.slice(0, groupsPerPage).map(key => groupedData[key][0]);
            updateMapMarkers(initialData);
        } else {
            // 데이터가 없으면 기존 마커 제거
            markersRef.current.forEach(marker => marker.setMap(null));
            markersRef.current = [];
            setLoading(false);
        }
    }, [auctionData, plnmKeys, groupsPerPage, groupedData, updateMapMarkers]);

    // 현재 페이지 데이터가 변경될 때마다 지도 마커 업데이트
    useEffect(() => {
        // 첫 로딩 시에는 이 훅이 실행되지 않도록 조건 추가
        if (auctionData.length > 0) {
            setLoading(true);
            updateMapMarkers(paginatedData);
        }
    }, [currentPage, paginatedData, auctionData.length, updateMapMarkers]);

    // 페이지 변경 시 확장된 행 상태 초기화
    useEffect(() => {
        setExpandedRows({});
    }, [currentPage]);

    // 컴포넌트 마운트 시 카카오 맵 초기화
    useEffect(() => {
        const { kakao } = window;
        if (!kakao) return;
        const container = document.getElementById('map');
        const options = {
            center: new kakao.maps.LatLng(37.566826, 126.9786567), // 서울 시청
            level: 3
        };
        const newMap = new kakao.maps.Map(container, options);
        setMap(newMap);
    }, []);

    // ------------------------------------
    //  Rendering
    // ------------------------------------
    return (
        <Container className="my-5">
            <h2 className="mb-4 text-center">온비드 캠코 경매 조회</h2>

            {/* 검색 섹션 */}
            <div className="search-section my-4 d-flex justify-content-end">
                <Form.Select className="me-2" value={sido} onChange={handleSidoChange} style={{ width: '150px' }}>
                    {cities.map(city => (
                        <option key={city} value={city}>{city}</option>
                    ))}
                </Form.Select>
                <Form.Select value={sgk} onChange={handleSgkChange} style={{ width: '150px' }}>
                    {districts[sido] && districts[sido].map(district => (
                        <option key={district} value={district}>{district}</option>
                    ))}
                </Form.Select>
            </div>

            {/* 지도 섹션 */}
            <div id="map" style={{ width: '100%', height: '400px', marginBottom: '20px', position: 'relative' }} className="border rounded">
                {loading && (
                    <div className="d-flex flex-column justify-content-center align-items-center" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}>
                        <ClipLoader color="#007bff" size={50} />
                        <p className="mt-2 text-dark">데이터 로딩 중...</p>
                    </div>
                )}
            </div>

            {/* 데이터 카드 리스트 */}
            {!loading && paginatedKeys.length > 0 && (
                <div className="auction-card-grid my-4">
                    {paginatedKeys.map(plnmNo => {
                        const firstItem = groupedData[plnmNo][0];
                        const isExpanded = !!expandedRows[plnmNo];
                        const yuchalCount = parseInt(firstItem.USCBD_CNT || 0);

                        return (
                            <div 
                                key={plnmNo} 
                                className={`auction-main-card ${isExpanded ? 'expanded' : ''}`}
                            >
                                <div 
                                    className="auction-main-card-header"
                                    onClick={() => handleRowClick(plnmNo)}
                                >
                                    <div>
                                        <span className="auction-notice-no">공고 {plnmNo}</span>
                                        <div className="auction-title-area">
                                            <div className="auction-title">{firstItem.CLTR_NM}</div>
                                            <div className="auction-address">{firstItem.NMRD_ADRS || '주소 정보 없음'}</div>
                                        </div>
                                    </div>
                                    <div className="auction-metrics">
                                        <div className="auction-metric-item">
                                            <span className="auction-metric-label">감정가</span>
                                            <span className="auction-metric-value highlight">
                                                {formatCurrency(firstItem.APSL_ASES_AVG_AMT)}
                                            </span>
                                        </div>
                                        <div className="auction-metric-item align-items-end">
                                            <span className="auction-metric-label">유찰 여부</span>
                                            <span className={`auction-badge-yuchal ${yuchalCount >= 3 ? 'many' : ''}`}>
                                                {yuchalCount}회 유찰
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="auction-details-drawer">
                                        <h6 className="fw-bold mb-3 text-secondary border-bottom pb-2">
                                            📋 공고 내 개별 매각 물건 목록 ({groupedData[plnmNo].length}건)
                                        </h6>
                                        <div className="auction-sub-card-grid">
                                            {groupedData[plnmNo].map((item, subIndex) => (
                                                <div key={subIndex} className="auction-sub-card">
                                                    <div className="auction-sub-header">
                                                        <span className="auction-sub-no">관리번호: {item.CLTR_MNMT_NO}</span>
                                                        <span className="auction-sub-status">{item.PBCT_CLTR_STAT_NM}</span>
                                                    </div>
                                                    <div className="auction-sub-title mb-3">{item.CLTR_NM}</div>
                                                    <div className="auction-sub-info-row">
                                                        <span className="auction-sub-info-label">소재지</span>
                                                        <span className="auction-sub-info-val text-end" style={{ maxWidth: '70%', wordBreak: 'break-all' }}>
                                                            {item.NMRD_ADRS || '-'}
                                                        </span>
                                                    </div>
                                                    <div className="auction-sub-info-row">
                                                        <span className="auction-sub-info-label">최저입찰가</span>
                                                        <span className="auction-sub-info-val text-primary">{formatCurrency(item.MIN_BID_PRC)}</span>
                                                    </div>
                                                    <div className="auction-sub-info-row">
                                                        <span className="auction-sub-info-label">최저입찰가율</span>
                                                        <span className="auction-sub-info-val text-danger">{item.FEE_RATE}%</span>
                                                    </div>
                                                    <div className="auction-sub-info-row">
                                                        <span className="auction-sub-info-label">입찰 마감</span>
                                                        <span className="auction-sub-info-val text-muted" style={{ fontSize: '0.8rem' }}>
                                                            {formatDateTime(item.PBCT_CLS_DTM)}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* 데이터가 없고 로딩 중이 아닐 때 메시지 */}
            {!loading && paginatedKeys.length === 0 && (
                <div className="text-center mt-5">
                    <p className="text-muted">해당 지역에 경매 물건이 없습니다.</p>
                </div>
            )}

            {/* 페이지네이션 */}
            {!loading && totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
                        {[...Array(totalPages).keys()].map(number => (
                            <Pagination.Item
                                key={number + 1}
                                active={number + 1 === currentPage}
                                onClick={() => setCurrentPage(number + 1)}
                            >
                                {number + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                </div>
            )}
        </Container>
    );
};

export { Auction };